import { createAppError } from '../../api/errors';
import { compareWalletDecimals } from '../wallets/types';
import {
  withdrawalDeliveryAttemptFixtures,
  withdrawalEventFixtures,
  withdrawalFixtures,
} from './fixtures';
import type { WithdrawalRepository } from './repository';
import type {
  CreateWithdrawalResolutionInput,
  WithdrawalApprovalSummary,
  WithdrawalDetail,
  WithdrawalListQuery,
  WithdrawalResolutionInput,
  WithdrawalResolutionPreview,
  WithdrawalRetryInput,
  WithdrawalSummary,
} from './types';

export interface MockWithdrawalRepositoryOptions {
  readonly mode?: 'success' | 'empty' | 'error';
  readonly retryError?: boolean;
  readonly resolutionPreviewError?: boolean;
  readonly resolutionError?: boolean;
}

// Stable, human-readable route identifiers resolve to the first fixture without duplicating
// fixture rows or altering list/pagination totals.
const withdrawalDetailAliases: Readonly<Record<string, string>> = {
  'wd-a': withdrawalFixtures[0]!.id,
};

export class MockWithdrawalRepository implements WithdrawalRepository {
  readonly #options: MockWithdrawalRepositoryOptions;
  #withdrawals: readonly WithdrawalDetail[];
  readonly #previews = new Map<
    string,
    { readonly fingerprint: string; readonly preview: WithdrawalResolutionPreview }
  >();
  readonly #retryCommands = new Map<
    string,
    {
      readonly fingerprint: string;
      readonly result: { readonly data: WithdrawalDetail; readonly requestId: string };
    }
  >();
  readonly #resolutionCommands = new Map<
    string,
    {
      readonly fingerprint: string;
      readonly result: { readonly data: WithdrawalApprovalSummary; readonly requestId: string };
    }
  >();

  constructor(options: MockWithdrawalRepositoryOptions = {}) {
    this.#options = options;
    this.#withdrawals = withdrawalFixtures.map((item) => ({ ...item }));
  }

  async list(query: WithdrawalListQuery, signal: AbortSignal) {
    abortIfNeeded(signal);
    if (this.#options.mode === 'error') throw mockError('MOCK_WITHDRAWAL_LIST_FAILED', 'list');
    const filtered =
      this.#options.mode === 'empty'
        ? []
        : this.#withdrawals.filter((item) => matches(item, query));
    const ordered = [...filtered].sort(
      (left, right) =>
        Date.parse(right[query.sort]) - Date.parse(left[query.sort]) ||
        left.id.localeCompare(right.id),
    );
    return slicePage(ordered, query.cursor, query.pageSize, 'withdrawal');
  }

  async get(withdrawalId: string, signal: AbortSignal) {
    abortIfNeeded(signal);
    if (this.#options.mode === 'error') throw mockError('MOCK_WITHDRAWAL_DETAIL_FAILED', 'detail');
    const resolvedId = withdrawalDetailAliases[withdrawalId] ?? withdrawalId;
    const item = this.#withdrawals.find((candidate) => candidate.id === resolvedId);
    if (item === undefined) throw mockError('WITHDRAWAL_NOT_FOUND', 'detail');
    return item;
  }

  async listEvents(
    withdrawalId: string,
    query: { readonly cursor: string; readonly pageSize: number },
    signal: AbortSignal,
  ) {
    abortIfNeeded(signal);
    if (this.#options.mode === 'error') throw mockError('MOCK_WITHDRAWAL_EVENTS_FAILED', 'events');
    return slicePage(
      withdrawalEventFixtures.filter((item) => item.withdrawalId === withdrawalId),
      query.cursor,
      query.pageSize,
      'event',
    );
  }

  async listDeliveryAttempts(
    withdrawalId: string,
    query: { readonly cursor: string; readonly pageSize: number },
    signal: AbortSignal,
  ) {
    abortIfNeeded(signal);
    if (this.#options.mode === 'error')
      throw mockError('MOCK_WITHDRAWAL_ATTEMPTS_FAILED', 'attempts');
    return slicePage(
      withdrawalDeliveryAttemptFixtures.filter((item) => item.withdrawalId === withdrawalId),
      query.cursor,
      query.pageSize,
      'attempt',
    );
  }

  async retry(withdrawalId: string, input: WithdrawalRetryInput, idempotencyKey: string) {
    if (this.#options.retryError === true) throw mockError('MOCK_WITHDRAWAL_RETRY_FAILED', 'retry');
    const fingerprint = JSON.stringify({ withdrawalId, input });
    const replay = this.#retryCommands.get(idempotencyKey);
    if (replay !== undefined) {
      if (replay.fingerprint === fingerprint) return replay.result;
      throw mockError('MOCK_WITHDRAWAL_IDEMPOTENCY_CONFLICT', 'retry');
    }
    requireUuid(idempotencyKey, 'retry');
    if (input.reason.trim().length === 0 || input.reason.length > 2000)
      throw mockError('MOCK_WITHDRAWAL_RETRY_INVALID', 'retry');
    const index = this.#withdrawals.findIndex((item) => item.id === withdrawalId);
    if (index < 0) throw mockError('WITHDRAWAL_NOT_FOUND', 'retry');
    const current = this.#withdrawals[index]!;
    if (!retryableStatuses.has(current.status))
      throw mockError('MOCK_WITHDRAWAL_RETRY_CONFLICT', 'retry');
    const updated: WithdrawalDetail = {
      ...current,
      status: 'RETRY_PENDING',
      updatedAt: '2026-07-23T12:00:00+07:00',
      version: String(BigInt(current.version) + 1n),
    };
    this.#withdrawals = this.#withdrawals.with(index, updated);
    const result = { data: updated, requestId: 'mock-withdrawal-retry-request' };
    this.#retryCommands.set(idempotencyKey, { fingerprint, result });
    return result;
  }

  async previewResolution(withdrawalId: string, input: WithdrawalResolutionInput) {
    if (this.#options.resolutionPreviewError === true)
      throw mockError('MOCK_WITHDRAWAL_RESOLUTION_PREVIEW_FAILED', 'resolution-preview');
    const withdrawal = this.#withdrawals.find((item) => item.id === withdrawalId);
    if (withdrawal === undefined) throw mockError('WITHDRAWAL_NOT_FOUND', 'resolution-preview');
    if (withdrawal.status !== 'MANUAL_REVIEW')
      throw mockError('MOCK_WITHDRAWAL_RESOLUTION_CONFLICT', 'resolution-preview');
    validateResolution(input, 'resolution-preview');
    const preview: WithdrawalResolutionPreview = {
      withdrawalId,
      resolution: input.resolution,
      amount: withdrawal.amount,
      currencyCode: withdrawal.currencyCode,
      previewHash: 'withdrawal_resolution_preview_hash_0000000',
    };
    this.#previews.set(withdrawalId, { fingerprint: resolutionFingerprint(input), preview });
    return preview;
  }

  async createResolution(
    withdrawalId: string,
    input: CreateWithdrawalResolutionInput,
    idempotencyKey: string,
  ) {
    if (this.#options.resolutionError === true)
      throw mockError('MOCK_WITHDRAWAL_RESOLUTION_FAILED', 'resolution');
    const commandFingerprint = JSON.stringify({ withdrawalId, input });
    const replay = this.#resolutionCommands.get(idempotencyKey);
    if (replay !== undefined) {
      if (replay.fingerprint === commandFingerprint) return replay.result;
      throw mockError('MOCK_WITHDRAWAL_IDEMPOTENCY_CONFLICT', 'resolution');
    }
    requireUuid(idempotencyKey, 'resolution');
    const withdrawal = this.#withdrawals.find((item) => item.id === withdrawalId);
    if (withdrawal === undefined) throw mockError('WITHDRAWAL_NOT_FOUND', 'resolution');
    if (withdrawal.status !== 'MANUAL_REVIEW')
      throw mockError('MOCK_WITHDRAWAL_RESOLUTION_CONFLICT', 'resolution');
    const { previewHash, ...previewInput } = input;
    validateResolution(previewInput, 'resolution');
    const preview = this.#previews.get(withdrawalId);
    if (
      preview === undefined ||
      preview.preview.previewHash !== previewHash ||
      preview.fingerprint !== resolutionFingerprint(previewInput)
    ) {
      throw mockError('MOCK_WITHDRAWAL_PREVIEW_STALE', 'resolution');
    }
    const result = {
      data: {
        id: `00000000-0000-4000-8000-${String(this.#resolutionCommands.size + 2401).padStart(12, '0')}`,
        partnerId: withdrawal.partner.id,
        actionType: input.resolution === 'COMPLETE' ? 'WITHDRAWAL_COMPLETE' : 'WITHDRAWAL_RELEASE',
        entityType: 'AFFILIATE_WITHDRAWAL',
        entityId: withdrawal.id,
        reason: input.reason,
        evidenceReference: input.evidenceReference,
        status: 'PENDING',
        requestedBy: '00000000-0000-4000-8000-000000000001',
        requestedAt: '2026-07-23T12:00:00+07:00',
        expiresAt: '2026-07-24T12:00:00+07:00',
        decidedBy: null,
        decidedAt: null,
        decisionReason: null,
        executedAt: null,
        executionReference: null,
      } satisfies WithdrawalApprovalSummary,
      requestId: 'mock-withdrawal-resolution-request',
    };
    this.#resolutionCommands.set(idempotencyKey, { fingerprint: commandFingerprint, result });
    return result;
  }
}

const retryableStatuses = new Set<WithdrawalDetail['status']>([
  'RETRY_PENDING',
  'CREDIT_STATUS_UNKNOWN',
  'MANUAL_REVIEW',
]);
const failureCodePattern = /^[A-Z][A-Z0-9_]{1,99}$/u;
const uuidV4Pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function validateResolution(input: WithdrawalResolutionInput, surface: string) {
  const commonValid =
    input.reason.trim().length > 0 &&
    input.reason.length <= 2000 &&
    input.evidenceReference.trim().length > 0 &&
    input.evidenceReference.length <= 1000;
  const specificValid =
    input.resolution === 'COMPLETE'
      ? input.partnerCreditId !== undefined &&
        input.partnerCreditId.trim().length > 0 &&
        input.partnerCreditId.length <= 200 &&
        input.failureCode === undefined
      : input.partnerCreditId === undefined &&
        input.failureCode !== undefined &&
        failureCodePattern.test(input.failureCode);
  if (!commonValid || !specificValid)
    throw mockError('MOCK_WITHDRAWAL_RESOLUTION_INVALID', surface);
}

function resolutionFingerprint(input: WithdrawalResolutionInput) {
  return JSON.stringify(input);
}

function requireUuid(value: string, surface: string) {
  if (!uuidV4Pattern.test(value)) throw mockError('MOCK_WITHDRAWAL_IDEMPOTENCY_INVALID', surface);
}

function matches(item: WithdrawalSummary, query: WithdrawalListQuery) {
  const f = query.filters;
  return (
    exact(item.partner.id, f.partnerId) &&
    exact(item.initiatingAgent?.id ?? null, f.agentId) &&
    exact(item.walletId, f.walletId) &&
    exact(item.memberId, f.memberId) &&
    exact(item.username, f.username) &&
    exact(item.partnerUserId, f.partnerUserId) &&
    exact(item.partnerWithdrawalId, f.partnerWithdrawalId) &&
    exact(item.transferId, f.transferId) &&
    exact(item.partnerCreditId, f.partnerCreditId) &&
    (f.currency === '' || item.currencyCode === f.currency.trim().toUpperCase()) &&
    (f.status === '' || item.status === f.status) &&
    exact(item.failureCode, f.failureCode) &&
    (f.deliveryResult === '' || item.latestDelivery?.normalizedResult === f.deliveryResult) &&
    (f.minimumAmount === '' || compareWalletDecimals(item.amount, f.minimumAmount) >= 0) &&
    (f.maximumAmount === '' || compareWalletDecimals(item.amount, f.maximumAmount) <= 0) &&
    timeRange(item.createdAt, f.createdFrom, f.createdTo) &&
    nullableTimeRange(item.completedAt, f.completedFrom, f.completedTo)
  );
}

function slicePage<Item>(items: readonly Item[], cursor: string, limit: number, prefix: string) {
  const match = new RegExp(`^mock-${prefix}-cursor-(\\d+)$`, 'u').exec(cursor);
  const start = match === null ? 0 : Number(match[1]);
  const pageSize = Math.max(1, Math.min(100, limit));
  const end = start + pageSize;
  return {
    items: items.slice(start, end),
    nextCursor: end < items.length ? `mock-${prefix}-cursor-${end}` : null,
    requestId: `mock-${prefix}-request`,
  };
}

function exact(value: string | null, filter: string) {
  return filter.trim() === '' || value === filter.trim();
}
function timeRange(value: string, from: string, to: string) {
  const time = Date.parse(value);
  return (
    (from === '' || time >= Date.parse(`${from}:00+07:00`)) &&
    (to === '' || time < Date.parse(`${to}:00+07:00`))
  );
}
function nullableTimeRange(value: string | null, from: string, to: string) {
  if (from === '' && to === '') return true;
  return value !== null && timeRange(value, from, to);
}
function abortIfNeeded(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
}
function mockError(code: string, surface: string) {
  return createAppError({
    status: 500,
    code,
    message: `Unable to load mock withdrawal ${surface}`,
    requestId: `mock-withdrawal-${surface}-request`,
    fieldErrors: {},
    retryAfterSeconds: null,
  });
}
